import { ListChecksIcon } from 'components/icons';
import { useDataTableRowVirtualizer } from 'components/tables/data-table/useDataTableRowVirtualizer';
import { findScrollParent } from 'components/tables/EndOfListSentinel';
import { Button } from 'components/ui/button';
import { Switch } from 'components/ui/switch';
import { ReactNode, Ref, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { cn } from 'utils/cn';

/** 36rem: a card narrower than this folds each row's figures under its entry and drops their columns. */
const NARROW_CARD_WIDTH = 576;

/** The Advanced billing column's `w-60`, which the card needs on top while it is on. */
const ADVANCED_COLUMN_WIDTH = 240;

/** Whether the card `cardRef` lands on is narrow, where each row folds its figures under its entry. */
export const useIsNarrowCard = (hasAdvancedColumn: boolean) => {
    const [card, setCard] = useState<HTMLElement | null>(null);
    const [isNarrow, setIsNarrow] = useState(false);
    const minWidth = NARROW_CARD_WIDTH + (hasAdvancedColumn ? ADVANCED_COLUMN_WIDTH : 0);
    useEffect(() => {
        if (!card) return undefined;
        const observer = new ResizeObserver(([entry]) => setIsNarrow(entry.contentRect.width < minWidth));
        observer.observe(card);
        return () => observer.disconnect();
    }, [card, minWidth]);
    return { cardRef: setCard, isNarrow };
};

/** The header row of an item table. */
export const ITEM_HEAD_ROW = 'border-divider text-foreground-tertiary text-meta border-y';

/** A header cell; `scope` is the caller's. */
export const ITEM_HEAD_CELL = 'px-2 py-2 text-start font-medium';

/** The selection column's cells: its header holds the select-all box. */
export const ITEM_SELECT_CELL = 'w-11 py-3.5 ps-5 pe-2 align-top';

/** The trailing column holding each row's Edit. */
export const ITEM_EDIT_CELL = 'w-14 py-2.5 ps-2 pe-4 text-end align-top';

/** The totals row closing the table, and an assignment's own. */
export const ITEM_TOTALS_ROW = 'border-divider bg-muted/40 border-b';

/** A figure in a totals row, or a row's billed hours. */
export const ITEM_FIGURE = 'text-body1 font-semibold tabular-nums';

/** A secondary figure under it: the used hours, the rate. */
export const ITEM_SUB_FIGURE = 'text-caption text-foreground-tertiary tabular-nums';

/** The title row's quiet actions. */
export const TITLE_ACTION = 'text-meta font-medium';

/** The title row's Bulk selection, which ticks rows by what they hold. */
export const ItemBulkSelectButton = ({ disabled, onClick }: { disabled: boolean; onClick: () => void }) => (
    <Button variant="ghost" size="sm" disabled={disabled} onClick={onClick} className={TITLE_ACTION}>
        <ListChecksIcon />
        <FormattedMessage id="invoices.tooltips.bulk-selection" />
    </Button>
);

/** The title row's Advanced billing switch, which adds the column of each row's advanced billing. */
export const AdvancedBillingToggle = ({
    checked,
    onCheckedChange,
    disabled,
}: {
    checked: boolean;
    onCheckedChange: (checked: boolean) => void;
    disabled: boolean;
}) => (
    <label className={`${TITLE_ACTION} flex cursor-pointer items-center gap-2`}>
        <FormattedMessage id="invoices.details.table.advanced-billing" />
        <Switch checked={checked} onCheckedChange={onCheckedChange} disabled={disabled} />
    </label>
);

/**
 * The header row's cells: select-all, the entry, its figure, Advanced billing while it is on, the amount, and Edit's
 * column. A narrow card keeps only select-all, the entry and Edit's column.
 */
export function ItemHeadCells({
    selectAll,
    entryLabelId,
    figureLabelId,
    isFigureEnd = false,
    hasAdvancedColumn,
    currency,
    isNarrow,
}: {
    /** The select-all box, while a row can be ticked. */
    selectAll: ReactNode;
    entryLabelId: string;
    figureLabelId: string;
    /** The figure's header sits at the end, over figures that do. */
    isFigureEnd?: boolean;
    hasAdvancedColumn: boolean;
    currency: string;
    isNarrow: boolean;
}) {
    return (
        <>
            <th scope="col" className={ITEM_SELECT_CELL}>
                {selectAll}
            </th>
            <th scope="col" className={ITEM_HEAD_CELL}>
                <FormattedMessage id={entryLabelId} />
            </th>
            {isNarrow ? null : (
                <>
                    <th scope="col" className={cn(ITEM_HEAD_CELL, 'w-40', isFigureEnd && 'text-end')}>
                        <FormattedMessage id={figureLabelId} />
                    </th>
                    {hasAdvancedColumn ? (
                        <th scope="col" className={`${ITEM_HEAD_CELL} w-60`}>
                            <FormattedMessage id="invoices.details.table.advanced-billing" />
                        </th>
                    ) : null}
                    <th scope="col" className={cn(ITEM_HEAD_CELL, 'w-32 text-end whitespace-nowrap')}>
                        <FormattedMessage id="invoices.details.table.amount-currency" values={{ currency }} />
                    </th>
                </>
            )}
            <th scope="col" className={ITEM_EDIT_CELL}>
                <span className="sr-only">
                    <FormattedMessage id="edit" />
                </span>
            </th>
        </>
    );
}

/** The seed height of a row before the list measures it: an entry, a two-line description and the padding. */
const ROW_ESTIMATE = 96;

/** A row only the window stands for: it holds the height of the rows above or below the rendered ones. */
const SpacerRow = ({ height, columnCount }: { height: number; columnCount: number }) => (
    <tr aria-hidden="true">
        <td colSpan={columnCount} className="p-0" style={{ height }} />
    </tr>
);

/**
 * The page's scroller and where the rows start in it, which the window counts from. The start moves whenever
 * something above the table changes size, so it is measured again on every resize of the page's content.
 */
const usePageScroll = () => {
    const bodyRef = useRef<HTMLTableSectionElement>(null);
    const [scroller, setScroller] = useState<HTMLElement | null>(null);
    const [scrollMargin, setScrollMargin] = useState(0);
    useLayoutEffect(() => {
        const body = bodyRef.current;
        if (!body) return undefined;
        const measure = () => {
            const page = findScrollParent(body);
            setScroller(page);
            if (page) {
                const offset = body.getBoundingClientRect().top - page.getBoundingClientRect().top + page.scrollTop;
                setScrollMargin(Math.round(offset));
            }
        };
        measure();
        const page = findScrollParent(body);
        const observer = new ResizeObserver(measure);
        if (page) [page, ...Array.from(page.children)].forEach((node) => observer.observe(node));
        window.addEventListener('resize', measure);
        return () => {
            observer.disconnect();
            window.removeEventListener('resize', measure);
        };
    }, []);
    return { bodyRef, scroller, scrollMargin };
};

/**
 * An invoice item list as a table: its caption, its header and its totals around a body windowed as `DataTable`'s, so
 * thousands of timecards render only the rows in view. The page is the scroller; the header and the totals stay
 * outside the window.
 */
export function ItemTable({
    caption,
    head,
    foot,
    columnCount,
    rowCount,
    getRowKey,
    renderRow,
}: {
    caption: string;
    /** The header row's cells. */
    head: ReactNode;
    /** The rows closing the table. */
    foot: ReactNode;
    columnCount: number;
    rowCount: number;
    getRowKey: (index: number) => string | number;
    /** One body row: its `<tr>` takes `measureRef` and `data-index={index}`, which the window measures it by. */
    renderRow: (index: number, measureRef: Ref<HTMLTableRowElement>) => ReactNode;
}) {
    const { bodyRef, scroller, scrollMargin } = usePageScroll();
    const getScrollElement = useCallback(() => scroller, [scroller]);
    const { virtualizer, measureRowRef } = useDataTableRowVirtualizer({
        rowCount,
        getScrollElement,
        estimateRowHeight: ROW_ESTIMATE,
        getItemKey: getRowKey,
        scrollMargin,
    });
    const items = virtualizer.getVirtualItems();
    // The items' offsets count from the top of the page's content; the spacers from the top of the rows.
    const before = (items[0]?.start ?? scrollMargin) - scrollMargin;
    const after = virtualizer.getTotalSize() - ((items.at(-1)?.end ?? scrollMargin) - scrollMargin);

    return (
        <div className="overflow-clip rounded-b-[10px]">
            <table className="w-full table-fixed border-collapse">
                <caption className="sr-only">{caption}</caption>
                <thead>
                    <tr className={ITEM_HEAD_ROW}>{head}</tr>
                </thead>
                <tbody ref={bodyRef}>
                    {before > 0 ? <SpacerRow height={before} columnCount={columnCount} /> : null}
                    {items.map((item) => renderRow(item.index, measureRowRef))}
                    {after > 0 ? <SpacerRow height={after} columnCount={columnCount} /> : null}
                </tbody>
                <tfoot>{foot}</tfoot>
            </table>
        </div>
    );
}

/**
 * The card of a case's timecards or expenses: its title row, which ticking rows swaps for the selection's actions,
 * and a polite status that says how many rows are ticked. With no rows it says so in place of its table.
 */
export function ItemCard({
    cardRef,
    titleId,
    title,
    count,
    caseLabel,
    titleExtras,
    actions,
    selectedCount,
    selectedMessage,
    selectionLabel,
    selectionActions,
    emptyMessage,
    children,
}: {
    cardRef: Ref<HTMLElement>;
    titleId: string;
    title: string;
    count: number;
    caseLabel: ReactNode;
    /** Beside the case: the price list. */
    titleExtras?: ReactNode;
    actions: ReactNode;
    selectedCount: number;
    /** "2 timecards selected": the status's words and the selection's title. */
    selectedMessage: string;
    /** The selection's actions' group name: "Actions for the selected timecards". */
    selectionLabel: string;
    selectionActions: ReactNode;
    emptyMessage: ReactNode;
    children: ReactNode;
}) {
    const isSelecting = selectedCount > 0;
    return (
        <section ref={cardRef} aria-labelledby={titleId} className="bg-card shadow-card rounded-[10px] outline-hidden">
            {isSelecting ? (
                <div
                    role="group"
                    aria-label={selectionLabel}
                    className="bg-primary-tint flex min-h-14 flex-wrap items-center gap-x-4 gap-y-2 rounded-t-[10px] px-5 py-2.5"
                >
                    <h2 id={titleId} className="text-section m-0">
                        {title}
                        <span className="text-meta font-semibold"> · {selectedMessage}</span>
                    </h2>
                    {/* On the tinted bar the outlined actions sit on the card's white with the control border. */}
                    <div className="[&>button]:border-border-control [&>button]:bg-card flex flex-wrap items-center gap-2">
                        {selectionActions}
                    </div>
                </div>
            ) : (
                <div className="flex min-h-14 flex-wrap items-center gap-x-4 gap-y-2 px-5 py-2.5">
                    <h2 id={titleId} className="text-section m-0 flex items-center gap-2">
                        {title}
                        <span className="bg-neutral-tint text-foreground-secondary inline-flex min-h-5 items-center rounded-lg px-1.5 text-xs font-medium tabular-nums">
                            {count}
                        </span>
                    </h2>
                    <span className="text-meta text-foreground-tertiary min-w-0">{caseLabel}</span>
                    {titleExtras}
                    <div className="ms-auto flex flex-wrap items-center gap-3">{actions}</div>
                </div>
            )}
            <p role="status" className="sr-only">
                {isSelecting ? selectedMessage : ''}
            </p>
            {count === 0 ? (
                <p className="text-body1 text-foreground-secondary border-divider m-0 border-t px-5 py-4">
                    {emptyMessage}
                </p>
            ) : (
                children
            )}
        </section>
    );
}

/** The case a card belongs to, as its title row names it: the number in mono, then the name. */
export const CaseLabel = ({ number, name }: { number?: string; name?: string }) => (
    <>
        {number ? <span className="font-mono">{number}</span> : null} {name}
    </>
);
